import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import { getStripeCustomer } from '@cartwhl/common/api/getStripeCustomer';
import {
  StripeTransaction,
  getStripeInvoices,
} from '@cartwhl/common/api/getStripeInvoices';
import { StripeCustomer } from '@cartwhl/common/types';
import { getStripePriceID } from '@cartwhl/common/utils';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';

import { userStore } from './global';

export type CustomerStoreState = {
  customer: StripeCustomer | null;
  loading: boolean;
  invoices: StripeTransaction[];
  invoicesLoading: boolean;
};

export const state = proxy<CustomerStoreState>({
  customer: null,
  loading: false,
  invoices: [],
  invoicesLoading: false,
});

const premiumProducts = new Set([
  getStripeProductID('pro'),
  getStripeProductID('advanced')
]);

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Customer', enabled: true });
}

/** Stable summary of subscription + billing fields that drive profile UI. */
function customerDataFingerprint(customer: StripeCustomer | null): string {
  if (!customer) return '';
  const plansKey = [...customer.plans]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((p) => `${p.id}:${p.product}:${p.active}`)
    .join('|');
  const pm = customer.invoice_settings?.default_payment_method;
  const card = pm?.card;
  const cardKey = card
    ? `${pm?.id ?? ''}:${card.brand}:${card.last4}:${card.exp_month}:${card.exp_year}`
    : '';
  const addr = customer.address;
  const addrKey = addr
    ? [
        addr.line1,
        addr.line2,
        addr.city,
        addr.state,
        addr.postal_code,
        addr.country,
      ]
        .map((x) => x ?? '')
        .join(':')
    : '';
  return `${plansKey}#${cardKey}#${addrKey}`;
}

export async function fetchCustomer(
  stripeCustomerID: string,
  options?: { force?: boolean; silent?: boolean },
) {
  const force = options?.force ?? false;
  const silent = options?.silent ?? false;
  if (!stripeCustomerID) return;

  if (!force) {
    if (state.loading || state.customer?.id === stripeCustomerID) return;
  }

  if (!silent) {
    state.loading = true;
  }

  const { data, error } = await getStripeCustomer(stripeCustomerID);

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    if (customerDataFingerprint(state.customer) !== customerDataFingerprint(data)) {
      state.customer = data;
    }
  }

  if (!silent) {
    state.loading = false;
  }
}

export async function fetchInvoices(stripeCustomerID: string) {
  if (state.invoicesLoading) return;

  state.invoicesLoading = true;

  const { data, error } = await getStripeInvoices(stripeCustomerID);

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    state.invoices = data.data;
  }

  state.invoicesLoading = false;
}

// Determines if the user has PREMIUM status (is a paying customer)
// via pro or unlimited plan subscription.
export function getSubscriberPremiumStatus(
  customer: StripeCustomer | null,
): 'isPaying' | 'willExpire' | 'expired' {
  if (!customer) {
    return 'expired';
  }

  const proPlans = customer.plans.filter((plan) => premiumProducts.has(plan.product));

  if (proPlans.length <= 0) {
    if (userStore.isPro() || userStore.isUnlimited()) {
      return 'willExpire';
    } else {
      return 'expired';
    }
  } else {
    if (proPlans.length > 0) {
      return 'isPaying';
    } else {
      return 'expired';
    }
  }
}

export function hasActiveSubscription(customer: StripeCustomer | null) {
  if (customer || state.customer) {
    return getSubscriberPremiumStatus(customer || state.customer) === 'isPaying';
  }
  return false;
}
